import React, { useState, useEffect, useRef } from 'react';
import { 
  Shield, ShieldCheck, Briefcase, FileText, MessageSquare, Settings, Search, Bell, User, Lock, LogOut, MoreVertical, 
  Download, Upload, Plus, CheckCircle2, Clock, Eye, Layers, X, ChevronRight, ChevronLeft, 
  FilePlus2, ArrowUp, ArrowDown, CheckSquare, CalendarDays, ArrowRight, Edit, Trash2, Save, Calendar, 
  MapPin, Gavel, Users, Receipt, Banknote, RefreshCw, Mail, Sliders, ArrowDownAZ, Image as ImageIcon, FileEdit, 
  MoveUp, MoveDown, RotateCw, Settings2, Database, UserCog, Scan, Printer, MonitorDown, Copy, FolderSync, Menu, Paperclip, ExternalLink, Folder, FolderOpen, UploadCloud
} from 'lucide-react';
import { ROLE_NAMES, formatHebDate, handleDownloadLocalFile } from '../utils';

export default function DocumentViewerModal({ doc, onClose }) {
  const ext = doc.name.split('.').pop().toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
  const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(ext);
  const isPdf = ext === 'pdf';
  const isDocx = ['docx', 'doc'].includes(ext);
  const isEml = ext === 'eml';

  const fileSrc = doc.fileUrl; 
  
  // EML parsing state
  const [emlData, setEmlData] = useState({ headers: null, body: 'טוען תוכן הודעה...', error: false });

  useEffect(() => {
    if (isEml && fileSrc) {
      fetch(fileSrc)
        .then(res => res.text())
        .then(text => {
          // מפענח EML בסיסי שקורא את כותרות האימייל והתוכן
          const subjectMatch = text.match(/^Subject:\s*(.*)/mi);
          const fromMatch = text.match(/^From:\s*(.*)/mi);
          const toMatch = text.match(/^To:\s*(.*)/mi);
          const dateMatch = text.match(/^Date:\s*(.*)/mi);
          
          const parts = text.split(/\r?\n\r?\n/);
          let bodyText = parts.slice(1).join('\n\n').substring(0, 5000); // הגבלת אורך לתצוגה

          setEmlData({
            headers: {
              Subject: subjectMatch ? subjectMatch[1] : 'ללא נושא',
              From: fromMatch ? fromMatch[1].replace(/[<>"']/g, '') : 'לא ידוע',
              To: toMatch ? toMatch[1].replace(/[<>"']/g, '') : 'לא ידוע',
              Date: dateMatch ? dateMatch[1] : ''
            },
            body: bodyText,
            error: false
          });
        })
        .catch(err => {
          setEmlData({ headers: null, body: 'שגיאה בטעינת תוכן האימייל.', error: true });
        });
    }
  }, [isEml, fileSrc]);

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[60] flex items-center justify-center p-0 sm:p-4 lg:p-8" dir="rtl">
      <div className="bg-slate-100 sm:rounded-2xl shadow-2xl w-full max-w-6xl h-full flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="bg-slate-800 px-4 py-3 flex justify-between items-center text-white shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-1.5 bg-slate-700 rounded-lg shrink-0"><FileText className="w-5 h-5 text-blue-400" /></div>
            <div className="min-w-0 pr-2">
              <p className="font-medium text-sm leading-tight truncate" dir="ltr">{doc.name}</p>
              <p className="text-xs text-slate-400 mt-0.5 truncate">{doc.size || ''} • ע"י: {doc.uploadedBy} • {formatHebDate(doc.date)}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {fileSrc && (
                <a href={fileSrc} download={doc.name} target="_blank" rel="noreferrer" className="text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 p-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium">
                    <Download className="w-4 h-4" /> <span className="hidden sm:inline">הורדה</span>
                </a>
            )}
            <div className="w-px h-6 bg-slate-600 mx-1 hidden sm:block"></div>
            <button onClick={onClose} className="text-slate-300 hover:text-red-400 bg-slate-700 hover:bg-slate-600 p-2 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
          </div>
        </div>
        
        <div className="flex-1 overflow-hidden bg-slate-200/80 flex items-center justify-center p-4">
           <div className="bg-white w-full h-full shadow-lg border border-slate-300 flex flex-col items-center justify-center text-slate-800 overflow-hidden relative rounded-lg">
              {doc.type === 'invoice' ? (
                <div className="max-w-md mx-auto mt-4 p-8 bg-slate-50 border border-slate-200 rounded-xl text-right shadow-sm w-full">
                  <h4 className="font-bold text-2xl text-slate-800 border-b border-slate-200 pb-4 mb-6 flex items-center gap-3"><Receipt className="w-6 h-6 text-blue-600"/> חשבונית מס / קבלה</h4>
                  <div className="space-y-3 text-lg">
                      <p className="text-slate-700"><strong>סכום:</strong> ₪{doc.invoiceDetails?.amount} {doc.invoiceDetails?.hasVat ? '(כולל מע"מ)' : '(לא כולל מע"מ)'}</p>
                      <p className="text-slate-700"><strong>אמצעי תשלום:</strong> {doc.invoiceDetails?.method}</p>
                      {doc.invoiceDetails?.refNumber && <p className="text-slate-700"><strong>אסמכתא:</strong> {doc.invoiceDetails?.refNumber}</p>}
                  </div>
                </div>
              ) : !fileSrc ? (
                <div className="text-center p-12">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-xl font-bold text-slate-600 mb-2">תצוגה מקדימה לא זמינה</p>
                    <p className="text-sm text-slate-500">מסמך זה נוצר כהדמיה (לפני חיבור ה-Storage האמיתי) ואינו מכיל קובץ מקור.</p>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-100/50">
                    {isImage && <img src={fileSrc} alt={doc.name} className="max-w-full max-h-full object-contain shadow-sm" />}
                    {isVideo && <video src={fileSrc} controls className="max-w-full max-h-full shadow-sm" />}
                    {isPdf && <iframe src={fileSrc} title={doc.name} className="w-full h-full border-0 shadow-sm" />}
                    {isDocx && (
                        fileSrc.startsWith('http') ? (
                            <iframe src={`https://docs.google.com/gview?url=${encodeURIComponent(fileSrc)}&embedded=true`} className="w-full h-full border-0 shadow-sm" />
                        ) : (
                            <div className="text-center p-12">
                               <FileText className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                               <p className="text-lg font-bold text-slate-700 mb-2">מסמך Word ({ext.toUpperCase()})</p>
                               <p className="text-sm text-slate-500 mb-4">תצוגה מקדימה לקבצי Word דורשת לעיתים הורדה.</p>
                               <a href={fileSrc} download={doc.name} className="bg-blue-600 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2 hover:bg-blue-700 transition-colors">
                                   <Download className="w-4 h-4"/> הורד קובץ לצפייה
                               </a>
                            </div>
                        )
                    )}
                    {isEml && (
                        <div className="w-full h-full bg-white flex flex-col text-right overflow-hidden">
                           {emlData.headers && (
                             <div className="bg-slate-50 border-b border-slate-200 p-6 shrink-0">
                                <h2 className="text-2xl font-bold text-slate-800 mb-4">{emlData.headers.Subject}</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600">
                                   <p><strong>מאת:</strong> <span dir="ltr">{emlData.headers.From}</span></p>
                                   <p><strong>אל:</strong> <span dir="ltr">{emlData.headers.To}</span></p>
                                   <p className="md:col-span-2"><strong>תאריך:</strong> <span dir="ltr">{emlData.headers.Date}</span></p>
                                </div>
                             </div>
                           )}
                           <div className="flex-1 p-6 overflow-auto">
                              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 max-w-4xl mx-auto break-words">{emlData.body}</pre>
                           </div>
                        </div>
                    )}
                    {(!isImage && !isVideo && !isPdf && !isDocx && !isEml) && (
                        <div className="text-center p-12">
                           <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                           <p className="text-lg font-bold text-slate-700 mb-2">קובץ להורדה</p>
                           <p className="text-sm text-slate-500 mb-4">לא ניתן להציג סוג קובץ זה ישירות בדפדפן.</p>
                           <a href={fileSrc} download={doc.name} className="bg-blue-600 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2 hover:bg-blue-700 transition-colors">
                               <Download className="w-4 h-4"/> הורד קובץ
                           </a>
                        </div>
                    )}
                </div>
              )}
           </div>
        </div>
      </div>
    </div>
  );
}
